/* SAGE Tool Documentation — comprehensive reference for all 39 Lean tools.
 *
 * Each entry follows the schema:
 *   {
 *     id, name, category, icon (lucide), tagline,
 *     purpose      : 1–2 sentence one-liner of what the tool does
 *     history      : where it came from, who developed it, decade
 *     whenToUse    : bullet list of triggers / scenarios
 *     howToUse     : ordered list of steps to run the wizard
 *     example      : worked example (string with paragraph + table-like ASCII or short narrative)
 *     outputs      : list of artefacts produced and how to interpret each
 *     pitfalls     : common mistakes / anti-patterns
 *     furtherReading: list of {label, ref} (book / article / standard)
 *     chart        : optional inline-svg key visual id (resolved by DocPage)
 *   }
 *
 * Authored to be ~250–400 words per tool — long enough to teach a new
 * consultant, short enough to read in 4–5 minutes before a workshop.
 */

const D = {
  staffing_assessment: {
    name: "Staffing & FTE Diagnostic",
    category: "Diagnose",
    icon: "Users",
    tagline: "Quantify the workforce gap between current and modelled-required FTE.",
    purpose:
      "Build a defensible bottom-up staffing model that converts process volumes, AHT, yield, utilisation and productivity into Required FTE per stage and surfaces the headcount delta against today.",
    history:
      "Rooted in industrial-engineering work-measurement traditions of Frederick Taylor (1900s) and the time-study refinements by the Gilbreths. The modern FTE-from-volume formulation was popularised by Maynard's Industrial Engineering Handbook (1963) and is now standard in BPO, banking back-office and shared-services capacity planning.",
    whenToUse: [
      "Onboarding a new client and need a baseline of where capacity is over- or under-deployed",
      "Justifying headcount during budgeting, restructuring or M&A integrations",
      "Designing workforce plans for new processes / volume scenarios",
      "Computing Emiratisation / localisation gaps in GCC engagements",
    ],
    howToUse: [
      "Setup → enter org profile, currency, localisation target & tolerance, total monthly volume.",
      "Effective hours → working days, holidays, leave (expat & local), shift hours, breaks. The tool calculates net productive hours / month.",
      "Stages → list each process step with AHT (min/unit), yield %, current local + expat counts, utilisation, productivity, priority.",
      "Calibration → tweak global utilisation/productivity overrides to reflect maturity (e.g. 65 % for new teams, 80 % for stable ones).",
      "Demand → 12-month volume profile + next-year delta % to project future capacity.",
      "Run Diagnostic → review delta, alerts, priority actions and 4-phase roadmap; export PDF/CSV.",
    ],
    example:
      "A 45-FTE Insurance claims team processes 30 000 claims/month at 8 min AHT, 92 % yield. The model returns 38.4 Required FTE → +6.6 surplus → US$ 99 000/month overstaffing, with 'Rework Rate 11 %' flagged as the dominant capacity sink (eliminating 50 % of rework would recover 4.2 FTE).",
    outputs: [
      "Headcount Δ KPI — sign + magnitude tells you if the priority is recruit or redeploy.",
      "Workforce intel — local %, target, conversion factor (expat:local hour ratio).",
      "Alerts — colour-graded by Rework, NVA, Absenteeism, Attrition, Over/Understaffing thresholds.",
      "Priority actions — top-3 stages weighted by Required FTE × priority.",
      "4-phase Lean roadmap — Diagnose & Stabilise → Quick Wins → Structural Redesign → Continuous Improvement.",
    ],
    pitfalls: [
      "Counting 8 hr shift as 480 productive minutes — ignore breaks, meetings, system downtime.",
      "Using one global utilisation across very different stages — calibrate per stage when bottlenecks differ.",
      "Forgetting yield % — if 8 % of cases rework, throughput is 92 % of arrival.",
      "Mixing expat & local without separate hours — leave entitlements differ materially in the GCC.",
    ],
    furtherReading: [
      { label: "Maynard's Industrial Engineering Handbook (5th ed.)", ref: "Zandin, 2001" },
      { label: "Workforce Planning: Strategies and Tools", ref: "SHRM, 2017" },
    ],
  },

  vsm_builder: {
    name: "Value Stream Mapping",
    category: "Diagnose",
    icon: "GitBranch",
    tagline: "See the whole flow — material, information, time and waste — on one page.",
    purpose:
      "Draw the end-to-end value stream so process steps, performers, queues, inventory, defects and pain-points are all visible together, enabling targeted improvement of the worst constraints first.",
    history:
      "Codified by Mike Rother & John Shook in 'Learning to See' (Lean Enterprise Institute, 1999) building on Toyota's 'Material & Information Flow' diagrams developed in the 1970s by Taiichi Ohno's industrial-engineering teams. Now mandated practice in Six Sigma Black Belt syllabi.",
    whenToUse: [
      "Kicking off a Lean transformation — map current state before designing future state",
      "Diagnosing throughput / lead-time complaints",
      "Identifying NVA, queues and bottleneck stages",
      "Aligning cross-functional teams around a shared picture of how work flows",
    ],
    howToUse: [
      "Define performers (V-lanes) and phases (H-lanes); the canvas auto-creates a matrix.",
      "Drag block types (Process / Decision / Wait / Storage) onto the right lane intersection — the tool snaps & tags Phase·Performer.",
      "Per block: capture cycle-time, change-over, uptime%, defect%, operators, inventory.",
      "Add pain-points to a block — severity (H/M/L), TIMWOOD waste tag, owner, est. cost.",
      "Toggle the density heatmap (Off / Blocks / Pain) to see workload or quality concentration.",
      "Review the pain-points roll-up table beneath the canvas; click a row to jump-edit.",
    ],
    example:
      "An invoice-processing stream had 14 blocks across 4 phases × 3 performers. The heatmap revealed 60 % of pain-points sat in 'Approval × Manager', driving a follow-up Standard Work + Approval-tier review that cut lead time from 9 → 4 days.",
    outputs: [
      "Current-state map (printable A4 landscape PDF) — the team's shared mental model.",
      "Pain-point roll-up — top severities × cost = your kaizen backlog.",
      "Density heatmap — concentration of waste shows where to focus.",
      "Block table grouped by Phase or Performer — drives load-balancing & redesign.",
    ],
    pitfalls: [
      "Mapping the 'official' process instead of the actual one observed at the gemba.",
      "Skipping cycle-time data — without numbers the map is just a picture.",
      "Not closing the loop with a future-state map and Kaizen burst plan.",
    ],
    furtherReading: [
      { label: "Learning to See", ref: "Rother & Shook, LEI 1999" },
      { label: "Value Stream Management for Lean Office", ref: "Tapping & Shuker, 2003" },
    ],
    chart: "vsm",
  },

  sipoc_mapper: {
    name: "SIPOC Mapper",
    category: "Diagnose",
    icon: "Columns",
    tagline: "Scope any process in 5 columns before you optimise it.",
    purpose:
      "Establish the high-level boundaries of a process — Suppliers, Inputs, Process steps, Outputs, Customers — to align stakeholders on scope before deep-diving into VSM or DMAIC analysis.",
    history:
      "Originally developed at IBM in the 1980s as part of Total Quality Management; widely adopted in Six Sigma Define-phase tollgates (Motorola, GE) in the 1990s. SIPOC's clarity made it the de-facto kickoff template for any process project.",
    whenToUse: [
      "Day 1 of any process improvement engagement",
      "Project chartering / DMAIC Define phase",
      "When stakeholders disagree on what is in/out of scope",
      "Before drawing detailed process maps or value stream maps",
    ],
    howToUse: [
      "Name the process and its purpose in one line.",
      "Customers — who receives the output? Add internal/external customers separately.",
      "Outputs — what does each customer get? (e.g. approved invoice, delivered policy).",
      "Process — list 4–7 high-level steps. Resist the urge to go granular.",
      "Inputs — what triggers and feeds the process? (data, materials, decisions).",
      "Suppliers — who provides each input? Note hand-off owners.",
    ],
    example:
      "Claims-payment SIPOC: Suppliers (broker, customer) → Inputs (claim form, evidence) → Process (Receive → Validate → Investigate → Adjudicate → Pay) → Outputs (payment, denial letter) → Customers (claimant, regulator). One workshop established why the Investigate ↔ Adjudicate hand-off (broker missing data) was the actual scope problem.",
    outputs: [
      "5-column canvas (PDF) signed off by sponsor — the official scope of the engagement.",
      "Balanced canvas indicator — flags asymmetry (e.g. 12 inputs, 1 output suggests scope creep).",
    ],
    pitfalls: [
      "Going too granular on Process — keep it to 4–7 steps; detail belongs in VSM.",
      "Conflating internal & external customers — they have different SLAs and escalation paths.",
      "Skipping Suppliers — most defects originate at the supply boundary.",
    ],
    furtherReading: [
      { label: "The Six Sigma Handbook (5th ed.)", ref: "Pyzdek & Keller, 2018" },
    ],
    chart: "sipoc",
  },

  voc_kano: {
    name: "Voice of Customer + Kano",
    category: "Diagnose",
    icon: "MessageSquare",
    tagline: "Translate customer voice into prioritised improvement opportunities.",
    purpose:
      "Capture raw customer feedback, translate it into Critical-to-Quality (CTQ) requirements, classify each by Kano category (Must-be / Performance / Delighter / Indifferent / Reverse) and rank by opportunity gap (importance × satisfaction shortfall).",
    history:
      "VOC was formalised by Yoji Akao in Quality Function Deployment (Japan, 1972). The Kano model was published by Noriaki Kano (Tokyo University of Science, 1984) — a breakthrough in showing that customer satisfaction is non-linear with feature delivery.",
    whenToUse: [
      "Designing a new product, service or experience",
      "Re-balancing improvement priorities when teams chase 'shiny features'",
      "Quantifying the 'gap to delight' to justify investment",
      "Cleaning up survey free-text into structured drivers",
    ],
    howToUse: [
      "Capture raw VOC — verbatim quotes from interviews, surveys, complaints, reviews.",
      "For each VOC, distil one CTQ — measurable, specific (e.g. 'first-call resolution > 80 %').",
      "Score importance (1–5) and current satisfaction (1–5) per CTQ.",
      "Classify each CTQ by Kano category — Must-be, Performance, Delighter, Indifferent, Reverse.",
      "Review the opportunity-gap ranking (importance × (5 − satisfaction)) — top of the list is your design backlog.",
    ],
    example:
      "Retail-banking onboarding study: 'I had to upload my passport twice' (VOC) → 'Single-upload onboarding' (CTQ, importance 5, satisfaction 1, Must-be). Gap score 20 — top priority. Conversely 'Welcome video' scored 2/3 Delighter — built later as a low-cost morale booster.",
    outputs: [
      "VOC ↔ CTQ table — auditable trail from quote to requirement.",
      "Kano-classified backlog — Must-bes are table-stakes, Performance attributes drive linear satisfaction, Delighters create loyalty.",
      "Opportunity-gap ranking — prioritises design effort by satisfaction shortfall × importance.",
    ],
    pitfalls: [
      "Treating Delighters today as Delighters tomorrow — they decay into Performance and then Must-be (e.g. mobile app).",
      "Asking customers what they want — observe what they actually do (Jobs-to-be-Done lens helps).",
      "Skipping the importance score — without it, the Kano classification can't drive priorities.",
    ],
    furtherReading: [
      { label: "Attractive Quality and Must-be Quality", ref: "Kano, 1984" },
      { label: "What Customers Want", ref: "Anthony Ulwick, 2005" },
    ],
    chart: "kano",
  },

  time_study: {
    name: "Process Observation / Time Study",
    category: "Diagnose",
    icon: "Timer",
    tagline: "Real timings, not averages — uncover hidden variability and waste.",
    purpose:
      "Decompose a process into activities, time multiple cycles, classify each as Value-Add / Non-Value-Add / Business-NVA, compute mean, range, CV % and total Average Handle Time so capacity planning rests on observed data, not assumption.",
    history:
      "Born out of Frederick Taylor's stopwatch studies (Bethlehem Steel, 1898); refined by the Gilbreths' therbligs (1909) and operationalised through MTM-1 / MOST predetermined-time systems (1948+). Today's digital time-study apps (e.g. Proplanner, Acumence) carry this lineage forward.",
    whenToUse: [
      "Validating AHT assumptions for staffing models",
      "Quantifying the VA share of a process — the Lean baseline",
      "Investigating cycle-time complaints from a customer",
      "Pre-Yamazumi line-balancing exercise",
    ],
    howToUse: [
      "List activities in execution order — keep them granular but not atomic.",
      "Time 6–12 cycles per activity (more if variability is high).",
      "Classify each activity VA / NVA / BNVA — VA changes form/fit/function for the customer; BNVA is required (regulatory/legal) but doesn't add customer value.",
      "Review the auto-computed mean, range, CV % per activity.",
      "Total AHT and VA-ratio (VA min / total min) is your headline KPI.",
    ],
    example:
      "Claims handling decomposed into 11 activities. Time study revealed 'Manual data entry from PDF' averaged 3.2 min with CV 41 % (high variability) and was 100 % NVA → automation candidate worth 28 FTE-days/month at 3 000 cycles.",
    outputs: [
      "Activity table with mean, range, CV % per step.",
      "AHT roll-up — feeds Staffing & Yamazumi tools.",
      "VA-ratio % — Lean target is > 25 % for office; world-class manufacturing > 50 %.",
      "High-CV % flag — variability targets for standardisation.",
    ],
    pitfalls: [
      "Hawthorne effect — observed workers slow down or speed up; brief them honestly and observe over multiple shifts.",
      "Sampling only the 'easy' cases — sample the full mix.",
      "Conflating BNVA and NVA — regulatory steps cannot be eliminated, only streamlined.",
    ],
    furtherReading: [
      { label: "Motion and Time Study", ref: "Barnes, 7th ed., 1980" },
      { label: "MOST Work Measurement Systems", ref: "Zandin, 2003" },
    ],
  },

  waste_walk: {
    name: "Waste Walk / Gemba Tracker",
    category: "Diagnose",
    icon: "MapPin",
    tagline: "See waste where the work happens — TIMWOODS at the gemba.",
    purpose:
      "Structured floor-walk that logs each instance of the 8 wastes (TIMWOODS — Transportation, Inventory, Motion, Waiting, Over-processing, Over-production, Defects, Skills under-use) with severity, owner, photo and cost impact.",
    history:
      "Taiichi Ohno's original Toyota Production System named 7 wastes (muda) in the 1950s. Womack & Jones added Skills under-use as the 8th in 'Lean Thinking' (1996). 'Going to Gemba' (the place where value is created) is one of the four Lean genchi-genbutsu pillars.",
    whenToUse: [
      "First week of any Lean engagement — walk before you talk",
      "Building the case for change with quantified, visual evidence",
      "Refreshing the Kaizen backlog quarterly",
      "Engaging frontline teams in waste identification",
    ],
    howToUse: [
      "Define the gemba route with the team (start ↔ end + areas to observe).",
      "Walk slowly. Stop. Watch one full cycle of each activity before moving.",
      "Log every observation: TIMWOODS waste type, location, severity (H/M/L), photo URL, root-cause hypothesis, suggested fix.",
      "Estimate cost impact — frequency × time × labour cost = US$/month.",
      "Review the 8-waste heat-map and top-cost observations with the team.",
    ],
    example:
      "Distribution-centre waste walk: 47 observations in 90 min. 'Operator walks 12 m to printer 6×/hr' (Motion) cost US$ 14 k/yr × 8 operators. Moving the printer was a 2-day fix recovering 0.6 FTE.",
    outputs: [
      "8-waste heat-map — concentrations point at chronic process failures.",
      "Top-cost observation list — quick-win backlog.",
      "Photo evidence — irrefutable when sponsoring change.",
      "TIMWOODS breakdown by location — where to send standard work.",
    ],
    pitfalls: [
      "Hunting for blame — focus on the system, not the person.",
      "Photographing without consent — confirm with operators and HR policy.",
      "Recording without estimating cost — without numbers, sponsors won't fund the fix.",
    ],
    furtherReading: [
      { label: "Toyota Production System: Beyond Large-Scale Production", ref: "Ohno, 1988" },
      { label: "Gemba Walks", ref: "Jim Womack, 2011" },
    ],
  },

  rca_5whys_fishbone: {
    name: "5 Whys + Fishbone RCA",
    category: "Diagnose",
    icon: "GitFork",
    tagline: "Drill from a symptom to its root cause — across all six Ms.",
    purpose:
      "Classify potential causes across the 6M categories (Manpower / Method / Machine / Material / Measurement / Mother Nature), then run a 5-Why drill on the most likely branches to reach actionable root causes; build a countermeasure plan with owner and due date.",
    history:
      "Fishbone (Ishikawa) diagram was created by Kaoru Ishikawa at Kawasaki shipyards in 1943 and standardised in his 1968 'Guide to Quality Control'. The 5-Why technique was developed by Sakichi Toyoda and adopted as a Toyota Production System core practice for andon-cord problem solving.",
    whenToUse: [
      "Investigating a defect, complaint or incident",
      "DMAIC Analyse phase",
      "After a Pareto analysis — drill into the vital few",
      "Pre-PFMEA — flesh out failure-mode causes",
    ],
    howToUse: [
      "Write the problem statement on the head of the fish — be specific & measurable.",
      "For each of the 6Ms, brainstorm causes (post-it style).",
      "Pick the 2–3 most likely causes; for each, run a 5-Why drill (Why? · Why? · Why? · Why? · Why?).",
      "Validate the root cause with data, not opinion.",
      "Define countermeasures with owner, due-date and a leading indicator to measure success.",
    ],
    example:
      "'Customer received the wrong shipment' → Why? Item picked from wrong bin → Why? Bin labels faded → Why? Labels printed on standard paper → Why? No spec for label durability → Why? No supplier audit. Root cause: missing supplier-spec governance. Countermeasure: durable label spec + quarterly supplier audit.",
    outputs: [
      "Fishbone diagram (PDF) — visual map of all hypotheses.",
      "5-Why chains — auditable cause-effect logic.",
      "Countermeasure plan — owner, due-date, KPI.",
    ],
    pitfalls: [
      "Stopping at 'human error' — that's a category, not a root cause.",
      "5-Why becoming 5-Who — focus on system causes, not blame.",
      "Skipping the data-validation step — opinion-driven RCA leads to weak fixes.",
    ],
    furtherReading: [
      { label: "Guide to Quality Control", ref: "Ishikawa, 1968" },
      { label: "The Toyota Way", ref: "Liker, 2004 (Ch. 13)" },
    ],
    chart: "fishbone",
  },

  pareto_analyzer: {
    name: "Pareto Analyzer",
    category: "Diagnose",
    icon: "BarChart2",
    tagline: "Find the vital few causes that drive 80 % of the problem.",
    purpose:
      "Apply Vilfredo Pareto's 80/20 principle to a count of defects, complaints or events — sort descending, plot cumulative share, and split the categories into the Vital Few (≤80 %) versus the Trivial Many.",
    history:
      "Vilfredo Pareto observed in 1896 that 80 % of Italian land was owned by 20 % of the population. Joseph Juran, in 'Quality Control Handbook' (1951), adapted this to quality management as the 'Pareto Principle' and made it a foundational Six Sigma tool.",
    whenToUse: [
      "Triaging a long list of defect categories or complaint types",
      "Justifying which 2–3 problems to investigate next",
      "DMAIC Analyse — narrowing scope before fishbone",
      "Showing executives a clear data-driven priority story",
    ],
    howToUse: [
      "List defect / event categories.",
      "Capture frequency (count) per category over an agreed window (e.g. 30 days).",
      "Auto-sort descending; the chart shows bars + cumulative-share line.",
      "Read the line crossing 80 % — categories to its left are the Vital Few.",
      "Send the Vital Few into RCA / 5-Why; defer or batch the Trivial Many.",
    ],
    example:
      "A telco found 312 complaint types in 30 days. Pareto showed 4 types (slow internet, billing error, outage, app crash) accounted for 78 %. The team focused only on those 4 → complaint volume fell 41 % in 8 weeks.",
    outputs: [
      "Sorted bar chart with cumulative-share line (PDF).",
      "Vital-Few category list — your investigation backlog.",
      "Vital-Few % of total — typical Pareto distributions show 70–90 % concentration.",
    ],
    pitfalls: [
      "Counting 'undefined' or 'other' as a category — rebucket first or it dominates the chart.",
      "Conflating frequency with cost — sometimes 1 high-cost defect beats 100 low-cost ones; consider $ Pareto separately.",
      "Re-running on the same window — if Vital Few categories shrink, re-Pareto to find the next ones.",
    ],
    furtherReading: [
      { label: "Quality Control Handbook (5th ed.)", ref: "Juran & Godfrey, 1999" },
    ],
    chart: "pareto",
  },

  spaghetti_diagram: {
    name: "Spaghetti / Flow Diagram",
    category: "Design",
    icon: "Spline",
    tagline: "Make wasted motion visible — measure metres, not minutes.",
    purpose:
      "Trace the physical (or digital) movement path of an operator, document or part on a layout to expose excess motion / transportation waste; quantify metres-per-cycle × cycles/day = motion waste.",
    history:
      "Originated in Frank Gilbreth's 'micro-motion study' film analyses (1910). Renamed 'spaghetti diagram' in 1950s Toyota workshops because the resulting tangle of paths resembled a plate of spaghetti.",
    whenToUse: [
      "Re-laying out a workspace, warehouse or office",
      "Pre/post comparison of a 5S re-organisation",
      "Investigating Motion-waste hypotheses from a Waste Walk",
      "Reducing operator fatigue and travel-time per cycle",
    ],
    howToUse: [
      "Upload (or sketch) the layout in the canvas grid.",
      "Click waypoints in execution order to plot the operator's path; the tool auto-computes inter-waypoint distance.",
      "Add multiple paths for different operators / scenarios — colour-coded.",
      "Capture cycles/day; total motion = path metres × cycles.",
      "Test layout alternatives by re-plotting on the same grid; compare metres saved.",
    ],
    example:
      "A pharmacy fill-station spaghetti showed an average 142 m walked per prescription × 240 fills/day = 34 km/day. Re-locating the printer + label rolls cut this to 47 m/fill → 11 km/day saved (≈ 47 min/operator/day).",
    outputs: [
      "Layout with overlaid colour-coded paths (PDF).",
      "Distance per leg, per cycle, per day — quantified motion waste.",
      "Before/after comparison if multiple iterations are saved.",
    ],
    pitfalls: [
      "Plotting 'expected' instead of observed paths — observe at gemba.",
      "Ignoring forklift / trolley constraints (one-way aisles, weight limits).",
      "Optimising one operator's path while disrupting another's — model the full team.",
    ],
    furtherReading: [
      { label: "Lean Lexicon (5th ed.)", ref: "LEI, 2014" },
    ],
  },

  pfmea: {
    name: "Process Failure Mode & Effects Analysis (PFMEA)",
    category: "Design",
    icon: "ShieldAlert",
    tagline: "Score risk before it ships — Severity × Occurrence × Detection.",
    purpose:
      "Identify every potential failure mode in a process, score Severity × Occurrence × Detection (1–10 each) → Risk Priority Number (RPN), then design preventive / detective controls and re-score post-action to quantify risk reduction.",
    history:
      "Developed by the US Military as MIL-P-1629 (1949) for aerospace reliability. Adopted by NASA's Apollo programme (1960s), then by Ford's Pinto post-mortems (1970s) and standardised by AIAG/VDA for automotive in 1993; now used in every safety-critical industry.",
    whenToUse: [
      "Designing a new process or major change",
      "Following a high-severity incident",
      "Regulatory / audit requirement (ISO 9001, ISO 13485, AIAG)",
      "Prioritising error-proofing investment",
    ],
    howToUse: [
      "List process steps, then per step list Failure Modes (what could go wrong).",
      "For each Failure Mode score Severity (1 = trivial, 10 = catastrophic), Occurrence (1 = rare, 10 = certain) and Detection (1 = automatic, 10 = invisible).",
      "RPN = S × O × D; the tool colour-codes Critical (≥200), High (≥100), Medium (≥50), Low.",
      "Define recommended actions for high-RPN modes — owner & due-date.",
      "After action implementation, re-score; the RPN-saved KPI demonstrates risk reduction.",
    ],
    example:
      "Onboarding-process PFMEA found 'KYC documents lost' at S=8, O=4, D=7 → RPN 224 (Critical). Action: digital uploads + auto-checksum. Re-score O=2, D=2 → RPN 32. Saved 192 RPN points and reduced legal exposure.",
    outputs: [
      "PFMEA register with current and post-action RPN per failure mode.",
      "RPN-saved KPI — total risk reduction across the register.",
      "Action plan with owners, due-dates and target RPN.",
    ],
    pitfalls: [
      "Inflating S/O/D scores with consensus pressure — anchor with examples.",
      "Treating Detection as 'we have a control' regardless of efficacy — measure detection rate.",
      "RPN cliff — a 199 RPN is not safe; review by Severity ≥ 8 separately.",
    ],
    furtherReading: [
      { label: "AIAG/VDA FMEA Handbook (2019)", ref: "AIAG, 2019" },
    ],
    chart: "rpn",
  },

  a3_problem_solving: {
    name: "A3 Problem Solving",
    category: "Design",
    icon: "FileSpreadsheet",
    tagline: "One page. Eight boxes. The whole story of a problem and its solution.",
    purpose:
      "Force a problem into a single A3 sheet structured as Background · Current State · Goal · Root Cause · Countermeasures · Implementation Plan · Verification · Follow-up — to drive disciplined PDCA thinking and one-page communication.",
    history:
      "A3 = ISO A3 paper size (297 × 420 mm). Developed at Toyota in the 1960s by Sakichi Toyoda's school of disciplined PDCA. Codified as a coaching tool in John Shook's 'Managing to Learn' (LEI, 2008).",
    whenToUse: [
      "Coaching a junior consultant / team-lead through a problem",
      "DMAIC Improve hand-off to operations",
      "Toyota-Kata cycles (target condition → obstacle → experiment)",
      "Replacing a 30-slide PPT with a single artefact",
    ],
    howToUse: [
      "Background — why does this problem matter to the business?",
      "Current State — measure the gap (with data).",
      "Goal — quantitative, time-bound (e.g. 'reduce lead time from 9 → 5 days by Q3').",
      "Root Cause — link to fishbone / 5-Why output.",
      "Countermeasures — list the experiments, expected vs actual.",
      "Implementation — Gantt, owner, dependency.",
      "Verification — leading & lagging KPI to confirm.",
      "Follow-up — 30/60/90-day review owner & cadence.",
    ],
    example:
      "Branch-banking A3: 'Loan approval lead-time' (current 7 days, goal 3 days). Root cause: 4 manual hand-offs. Countermeasures: workflow tool + delegation matrix. Result: 2.4 days at 60-day review; A3 now reused as the standard playbook for 22 branches.",
    outputs: [
      "Single A3 sheet (PDF, landscape) — the artefact.",
      "Completion meter — % of boxes filled, used by the coach to spot weak thinking.",
    ],
    pitfalls: [
      "Treating boxes as fields to fill — the value is the iterative thinking.",
      "Skipping verification because the project 'feels done'.",
      "Letting countermeasures balloon — keep to ≤ 5 testable experiments.",
    ],
    furtherReading: [
      { label: "Managing to Learn", ref: "John Shook, LEI 2008" },
    ],
  },

  dmaic_charter: {
    name: "DMAIC Project Charter",
    category: "Design",
    icon: "Workflow",
    tagline: "The Six-Sigma blueprint — Define, Measure, Analyse, Improve, Control.",
    purpose:
      "Structure a Lean Six-Sigma project across five phases with deliverables, owners, due-dates and a phase-gated progress meter so projects don't skip critical thinking steps.",
    history:
      "DMAIC was formalised by Mikel Harry & Bill Smith at Motorola (1986); refined by Jack Welch's GE Six Sigma deployment (1995). Today the dominant framework for Six-Sigma Black Belt projects globally.",
    whenToUse: [
      "Black-belt or green-belt project intake",
      "Variation-reduction or defect-elimination programmes",
      "Any complex problem benefiting from a phase-gated approach",
    ],
    howToUse: [
      "Define — problem statement, business case, scope, team, sponsor.",
      "Measure — current-state metrics, MSA, baseline.",
      "Analyse — root cause hypotheses validated with data (fishbone, regression, hypothesis test).",
      "Improve — pilot countermeasures, design of experiments, pilot results.",
      "Control — control plan, SPC, audit cadence, hand-over.",
      "Tick deliverables; progress bars per phase show project health.",
    ],
    example:
      "Manufacturing DMAIC reduced casting porosity from 6.2 % to 0.9 %. Define-phase scoping showed 80 % of defects in one mould; Measure-phase MSA found the visual inspection had only 67 % R&R; Improve installed an automated CT-scan gate; Control sustained the gain for 14 months.",
    outputs: [
      "DMAIC charter (PDF) signed by sponsor.",
      "Per-phase deliverable checklist + progress %.",
      "Hand-off document at Control phase for ops.",
    ],
    pitfalls: [
      "Skipping Measure-phase MSA — you can't trust analysis on flaky data.",
      "Letting Improve drag — set a 90-day max for a green-belt scope.",
      "Closing without Control plan — gains decay within 6 months.",
    ],
    furtherReading: [
      { label: "Lean Six Sigma for Service", ref: "Michael L. George, 2003" },
    ],
  },

  yamazumi: {
    name: "Yamazumi — Takt / Cycle / Line Balancing",
    category: "Design",
    icon: "BarChart3",
    tagline: "Visualise station load against Takt — find bottlenecks at a glance.",
    purpose:
      "Compute Takt time from customer demand and available time, decompose each station's elements (VA / NVA / BNVA / WAIT), plot stacked bars against the Takt line and surface bottleneck / under-loaded stations to inform line rebalancing.",
    history:
      "Yamazumi (Japanese 'pile up') chart developed at Toyota for assembly-line balancing in the 1960s; popularised in the West by 'The Toyota Way' (Liker, 2004) and the LEI's 'Lean Lexicon'.",
    whenToUse: [
      "Designing or rebalancing a line, cell or workflow",
      "After demand changes — re-Takt and rebalance",
      "Quantifying takt-loss from imbalance / waiting",
      "Showing operator load distribution to executives",
    ],
    howToUse: [
      "Set demand (units / period) and available net time → tool computes Takt.",
      "List stations; per station list elements with element time and VA classification.",
      "Read the SVG stacked-bar chart — each station vs the Takt line.",
      "Stations above Takt = Bottleneck (red). Far below = Under-loaded (amber).",
      "Re-allocate elements between stations to flatten the chart toward Takt.",
      "Read required-operators = Σ element-time / Takt — your minimum staffing.",
    ],
    example:
      "Mortgage-back-office balancing: 9 stations, Takt 14 min. Yamazumi showed Station 4 at 21 min (Bottleneck) while 7 was at 6 min. Moving 2 elements from 4 → 7 reduced Station 4 to 13 min and recovered 1.6 FTE/day.",
    outputs: [
      "Yamazumi stacked-bar chart with Takt line.",
      "Station status badges (Bottleneck / Balanced / Under-loaded).",
      "Required-operators KPI.",
    ],
    pitfalls: [
      "Forgetting changeover or break-time — bake into available net time.",
      "Treating WAIT as a fixed cost — it's the largest re-balance lever.",
      "Balancing the chart while ignoring skill / certification constraints — verify ILUO matrix coverage.",
    ],
    furtherReading: [
      { label: "Lean Lexicon (5th ed.)", ref: "LEI, 2014" },
      { label: "The Toyota Way (2nd ed.)", ref: "Liker, 2020" },
    ],
    chart: "yamazumi",
  },

  impact_effort: {
    name: "Impact × Effort Prioritisation",
    category: "Design",
    icon: "LayoutGrid",
    tagline: "2×2 triage — what to do this sprint vs what to plan vs what to drop.",
    purpose:
      "Plot ideas / improvements on a 2×2 of Impact (1–5) × Effort (1–5) into Quick Wins, Big Bets, Fill-ins or Time Sinks — a fast triage when the team has more ideas than capacity.",
    history:
      "Decision-matrix variant of the Eisenhower urgency-importance grid (1954). Adopted as a Lean kaizen-prioritisation tool in the 1990s and made ubiquitous by McKinsey's 2x2 culture in management consulting.",
    whenToUse: [
      "End-of-Kaizen idea backlog needs triage",
      "Roadmap planning meetings",
      "Choosing which RCA countermeasures to implement",
      "Steering-committee de-escalation — visualise effort tradeoff",
    ],
    howToUse: [
      "List ideas; for each score Impact (1–5) and Effort (1–5).",
      "The tool auto-places into one of four quadrants on a tinted scatter:",
      "  • Quick Wins (high impact, low effort) — do now.",
      "  • Big Bets (high impact, high effort) — plan and resource.",
      "  • Fill-ins (low impact, low effort) — interns / continuous improvement.",
      "  • Time Sinks (low impact, high effort) — reject or defer.",
    ],
    example:
      "Operations team triaged 28 kaizen ideas; 8 landed in Quick Wins (avg 9 days delivery), 4 in Big Bets (programmed for next quarter), 11 in Fill-ins (CI team), 5 in Time Sinks (rejected) — saving 3 weeks of debate.",
    outputs: [
      "Tinted-quadrant scatter chart with idea labels.",
      "Per-quadrant grouped lists for backlog / planning.",
    ],
    pitfalls: [
      "Letting senior voices anchor the scoring — score independently then average.",
      "Ignoring strategic alignment — impact is sometimes long-term, not immediate.",
      "Letting Quick Wins crowd out Big Bets that move the needle.",
    ],
    furtherReading: [
      { label: "The 7 Habits of Highly Effective People (Q1–Q4)", ref: "Covey, 1989" },
    ],
    chart: "impact_effort",
  },

  kaizen_charter: {
    name: "Kaizen Event Charter",
    category: "Design",
    icon: "Zap",
    tagline: "Five days. One process. Concrete improvement.",
    purpose:
      "Plan and run a focused 3–10 day Kaizen event — a structured rapid-improvement workshop that takes a process from baseline to redesigned + piloted in one short cycle.",
    history:
      "Kaizen ('change for the better') was popularised by Masaaki Imai in 'Kaizen: The Key to Japan's Competitive Success' (1986). Toyota standardised the multi-day Kaizen event format in the 1970s; it became a Western consulting staple via Shingijutsu and Boeing 'moonshine' workshops in the 1990s.",
    whenToUse: [
      "When a team has identified one focused process problem to attack",
      "Building Lean capability through learning-by-doing",
      "Demonstrating change velocity to skeptical sponsors",
    ],
    howToUse: [
      "Problem — frame the gap with data.",
      "Scope & Team — boundaries, sponsor, participants, sub-team leads.",
      "Metrics — baseline & target leading + lagging KPI.",
      "Experiments — current-state map, brainstorm, PICK chart, pilot solution.",
      "Outcomes — before/after metrics, sustainability plan, follow-up A3.",
    ],
    example:
      "Healthcare claims Kaizen reduced rework from 18 % to 4 % in 5 days: workshop ran VSM, identified two missing form fields, designed a digital form with mandatory validation, piloted Friday morning, signed off by sponsor at noon.",
    outputs: [
      "Kaizen charter (PDF) — pre-event briefing.",
      "After-action report with before/after metrics.",
      "Sustainability plan + 30-day follow-up.",
    ],
    pitfalls: [
      "No sponsor in the room on Day 1 or Day 5 — energy collapses.",
      "Scope too broad — keep it to one cell / one product / one shift.",
      "No follow-up — gains decay within 4–6 weeks without LSW & audits.",
    ],
    furtherReading: [
      { label: "Kaizen: The Key to Japan's Competitive Success", ref: "Imai, 1986" },
    ],
  },

  lean_wave_charter: {
    name: "Lean Wave Programme Charter",
    category: "Implement",
    icon: "Waves",
    tagline: "Multi-quarter Lean transformation — Wave 1 / 2 / 3 governance.",
    purpose:
      "Plan a multi-quarter Lean transformation as sequenced waves (typical 6–9 months each), each with milestones, RAG status, dependencies, sponsor and a benefit case — so the programme stays governable beyond a single Kaizen event.",
    history:
      "Wave-based deployment was pioneered by GE during its 1995 Six-Sigma roll-out and refined by McKinsey's Operations practice in the 2000s; today's 'agile-meets-Lean' cadence borrows from SAFe and ITIL change governance.",
    whenToUse: [
      "Enterprise-wide transformation engagement",
      "Cross-function programmes spanning multiple quarters",
      "When Kaizen events are accumulating without programme oversight",
      "Setting a board-level programme with steering committee reporting",
    ],
    howToUse: [
      "Per wave: list 4–8 milestones, owner, due-date, RAG.",
      "Capture programme-level KPIs (savings, NPS, process cycle time).",
      "Cross-link to underlying Kaizen / DMAIC projects in this app.",
      "Run weekly RAG-status updates — auto rolling-up to programme view.",
    ],
    example:
      "GCC bank Lean Programme: Wave 1 (Branch Ops, 9 mo) achieved US$ 14 M savings; Wave 2 (Contact-Centre, 6 mo) on track at amber; Wave 3 (Risk & Compliance, 9 mo) in design — total programme savings forecast US$ 41 M by end-Y2.",
    outputs: [
      "Programme charter PDF.",
      "Wave RAG dashboard.",
      "Benefit roll-up vs commitment.",
    ],
    pitfalls: [
      "RAG inflation — define quantitative thresholds for amber/red.",
      "Waves running in parallel without dependency mapping — bottleneck the same SMEs.",
      "Charter signed but not refreshed — review at every steering committee.",
    ],
    furtherReading: [
      { label: "The Lean Strategy", ref: "Ballé et al., 2017" },
    ],
  },

  roi_calculator: {
    name: "Benefit Case / ROI Calculator",
    category: "Implement",
    icon: "Calculator",
    tagline: "OPEX/CAPEX cash flow → NPV, ROI, payback.",
    purpose:
      "Build a defensible business case for a Lean / digital initiative — model OPEX & CAPEX cash flows over 3–5 years, compute discounted NPV, ROI %, and payback period to support investment approval.",
    history:
      "Discounted Cash-Flow analysis was formalised by Irving Fisher (1907) and John Burr Williams (1938). Adopted as the standard project-finance framework by McKinsey & Company in the 1960s and now mandated in most large corporates' capital approval processes.",
    whenToUse: [
      "Sponsoring an automation or process redesign with capital ask",
      "Comparing alternative solution scenarios on a like-for-like basis",
      "Steering-committee gate review",
      "Benefit-realisation baseline",
    ],
    howToUse: [
      "Capture year-by-year OPEX savings and CAPEX investments.",
      "Set the discount rate (typical 8–12 % for stable corporates, 15–20 % for emerging).",
      "Tool computes NPV, ROI %, payback period.",
      "Run sensitivity on discount rate and savings ramp to test robustness.",
    ],
    example:
      "RPA business case: US$ 320 k Yr-0 CAPEX, US$ 180 k/yr OPEX savings ramp from Yr-1. At 10 % discount → NPV US$ 372 k, ROI 116 %, payback 1.8 yr — approved at first review.",
    outputs: [
      "5-year cash flow PDF.",
      "NPV / ROI / Payback KPIs.",
      "Sensitivity table.",
    ],
    pitfalls: [
      "Counting paper savings (FTE on a chart) before they're released — committee will discount.",
      "Using a discount rate not aligned with corporate WACC.",
      "Ignoring ongoing run-cost (license / support) of the new solution.",
    ],
    furtherReading: [
      { label: "Valuation: Measuring and Managing the Value of Companies", ref: "Koller et al., 2020" },
    ],
  },

  idea_bank: {
    name: "Initiative / Idea Bank",
    category: "Implement",
    icon: "Inbox",
    tagline: "Submit → triage → approve → track. Simple workflow, big roll-up.",
    purpose:
      "Capture employee improvement ideas in a single inbox with status workflow (Submitted → Reviewed → Approved → In-Progress → Done / Rejected), benefit estimate per idea, and aggregated programme roll-up.",
    history:
      "Originated in Toyota's Soichiro Honda-era 'employee suggestion system' (1951), now part of the kaizen suggestion-system canon described in 40 Years, 20 Million Ideas (Yasuda, 1991).",
    whenToUse: [
      "Engaging frontline teams in continuous improvement",
      "Building a backlog for Lean teams to action",
      "Reinforcing the cultural change of 'every idea matters'",
    ],
    howToUse: [
      "Anyone submits ideas with description, expected benefit, owner.",
      "Reviewers move ideas through statuses; approvers add benefit & due-date.",
      "Closed ideas roll up into total benefit and adoption rate.",
    ],
    example:
      "Insurance back-office captured 412 ideas in 12 months; 187 implemented, US$ 2.1 M annualised savings, 72 % participation rate.",
    outputs: ["Idea register PDF.", "Status funnel.", "Benefit roll-up."],
    pitfalls: [
      "Ideas with no review SLA decay — set a 14-day max in 'Submitted'.",
      "Rewarding only big ideas kills small-improvement culture — celebrate breadth.",
    ],
    furtherReading: [
      { label: "40 Years, 20 Million Ideas", ref: "Yuzo Yasuda, 1991" },
    ],
  },

  standard_work: {
    name: "Standard Work (SWD)",
    category: "Implement",
    icon: "ListChecks",
    tagline: "Document the best-known way — and the only way until it's beaten.",
    purpose:
      "Author step-by-step Standard Work documents with sequence, takt time, cycle time per step and load-balance check — the foundation of stable, improvable processes.",
    history:
      "Standard Work is the third pillar of Toyota's TPS (alongside JIT and Jidoka). Codified by Taiichi Ohno (1978) and operationalised at scale by Bunji Tozawa's 1980s Japanese kaizen consulting practice.",
    whenToUse: [
      "Stabilising a process before measuring improvement",
      "Onboarding new operators",
      "Capturing a Kaizen-event redesigned process",
      "Pre-LSW (Leader Standard Work) and Layered Audits",
    ],
    howToUse: [
      "List steps with sequence, time, key points (safety / quality), tools required.",
      "Total cycle time vs Takt — flag if over.",
      "Photo or sketch per step where helpful.",
    ],
    example:
      "Surgical-instrument tray pack: 22 steps, 4 min 12 sec total vs Takt 4 min 30 sec — load-balanced. Standard Work poster posted at station; defect rate fell from 1.8 % → 0.3 %.",
    outputs: ["Standard Work Document (PDF) — wall-postable.", "Takt vs cycle-time check."],
    pitfalls: [
      "Writing SW from a desk, not the gemba.",
      "Locking SW so improvements can't be made — make it 'best known method, today'.",
    ],
    furtherReading: [
      { label: "Toyota Kata", ref: "Mike Rother, 2009" },
    ],
  },

  poka_yoke: {
    name: "Poka-Yoke Catalogue",
    category: "Implement",
    icon: "Shield",
    tagline: "Mistake-proofing devices — Prevention beats Detection beats Inspection.",
    purpose:
      "Catalogue mistake-proofing devices for a process, classify each as Prevention or Detection, capture cost, defect coverage and effectiveness — to hard-wire quality into the process design.",
    history:
      "Coined by Shigeo Shingo at Toyota in 1961 ('baka-yoke' renamed politely to 'poka-yoke' = 'mistake-proofing'). 'Zero Quality Control' (Shingo, 1986) is the canonical source.",
    whenToUse: [
      "After PFMEA — preventing high-RPN failure modes",
      "Reducing dependence on inspection",
      "Operator-error-driven defect categories",
    ],
    howToUse: [
      "Per failure mode list candidate poka-yoke devices.",
      "Classify Prevention (stops error happening) vs Detection (catches error after).",
      "Capture cost, defect coverage % and implementation owner.",
    ],
    example:
      "Pharma packaging: barcode scanner (Detection, US$ 4 k, 99 % coverage) + feeder geometry (Prevention, US$ 12 k, 100 % coverage). Combined zero-defect outcome justified the investment within 7 months.",
    outputs: ["Poka-yoke catalogue PDF.", "Coverage % vs defect categories."],
    pitfalls: [
      "Adding inspection and calling it poka-yoke — true PY makes the error impossible or auto-detectable.",
    ],
    furtherReading: [
      { label: "Zero Quality Control: Source Inspection and the Poka-Yoke System", ref: "Shingo, 1986" },
    ],
  },

  kanban_designer: {
    name: "Kanban / Pull System Designer",
    category: "Implement",
    icon: "LayoutKanban",
    tagline: "Compute WIP limits, container sizes and Kanban-card counts.",
    purpose:
      "Design a pull system — calculate Kanban card / container counts from demand, lead time, container size and safety factor; set WIP limits per stage to expose flow constraints.",
    history:
      "Kanban (Japanese 'signal card') developed by Taiichi Ohno at Toyota in 1953 by observing supermarket shelf-replenishment in Detroit. Adapted to knowledge work by David Anderson (Microsoft) in 2010 (Kanban book).",
    whenToUse: [
      "Eliminating over-production (Lean's #1 waste)",
      "Stabilising supply between stages with variability",
      "Knowledge-work flow management (digital Kanban boards)",
    ],
    howToUse: [
      "Capture demand rate, replenishment lead time, container size, safety factor.",
      "Cards = (Demand × Lead time × (1 + Safety)) / Container size.",
      "Per stage set WIP limits to expose bottlenecks.",
    ],
    example:
      "Auto-parts distributor: 240 units/day demand, 2-day lead, 12 units/container, 20 % safety → 48 cards. WIP limit revealed a 14-day FG pile-up that vanished in 30 days.",
    outputs: ["Kanban card calculation table.", "WIP-limit matrix."],
    pitfalls: [
      "Sizing cards once and forgetting — recompute when demand or lead time shifts > 15 %.",
      "WIP limits enforced without daily standup — they become aspirational.",
    ],
    furtherReading: [
      { label: "Kanban: Successful Evolutionary Change", ref: "David Anderson, 2010" },
    ],
  },

  smed: {
    name: "SMED (Single-Minute Exchange of Die)",
    category: "Implement",
    icon: "Timer",
    tagline: "Convert internal setup steps to external — slash changeover time.",
    purpose:
      "Reduce changeover (setup) time by classifying every step as Internal (must be done while machine is stopped) vs External (can be done while it's running) and converting Internal → External; the 'single-minute' goal is < 10 minutes.",
    history:
      "Developed by Shigeo Shingo at Toyota over 19 years (1950–1969); reduced a Mazda press changeover from 4 hours to 3 minutes. Codified in Shingo's 1985 book 'A Revolution in Manufacturing: The SMED System'.",
    whenToUse: [
      "Long machine / system changeovers limiting product mix",
      "Increasing batch flexibility (smaller economic batch)",
      "Reducing inventory by enabling more frequent changeovers",
    ],
    howToUse: [
      "Time current changeover end-to-end. Video-record if possible.",
      "List every step; classify Internal / External.",
      "For each Internal, ask 'can this be External?' — pre-stage tools, pre-heat, etc.",
      "For remaining Internal, simplify (clamps not bolts, modular fixtures).",
      "Re-time post-changes; the SMED yield is the % reduction.",
    ],
    example:
      "Plastic-injection moulding changeover 47 min → 11 min after SMED — eliminated 4 internal steps via pre-staged moulds and quick-release clamps; enabled doubling SKU mix with no extra capacity.",
    outputs: ["Internal/External classification table.", "Reduction yield %."],
    pitfalls: [
      "Optimising once and letting drift creep back — audit changeover monthly.",
      "Optimising only one machine — line balance shifts the bottleneck.",
    ],
    furtherReading: [
      { label: "A Revolution in Manufacturing: The SMED System", ref: "Shingo, 1985" },
    ],
  },

  kpi_scorecard: {
    name: "KPI / OpEx Scorecard Builder",
    category: "Sustain",
    icon: "Activity",
    tagline: "Tiered KPI dashboards — Target vs Actual vs RAG with owners.",
    purpose:
      "Design a tiered KPI scorecard — typically Tier-1 (operator), Tier-2 (supervisor), Tier-3 (manager) — with each metric having a target, actual, RAG threshold and named owner, to make performance visible and ownership unambiguous.",
    history:
      "Kaplan & Norton's Balanced Scorecard (HBR 1992) seeded the structured KPI movement; Lean's hoshin-kanri (policy deployment) added cascading-tier discipline at Toyota in the 1960s; OKR variants (Doerr, 2018) blend the two for tech orgs.",
    whenToUse: [
      "Building daily / weekly performance management cadence",
      "After Lean rollout — to sustain gains via measurement",
      "Aligning cross-tier conversations with shared metrics",
    ],
    howToUse: [
      "List KPIs per tier with owner, target, RAG bands.",
      "Capture actuals at agreed cadence.",
      "Status auto-computed (Green / Amber / Red).",
    ],
    example:
      "Bank branch scorecard: Tier-1 (FCR %, NPS), Tier-2 (Queue time, Loan TAT), Tier-3 (Cost-per-account, Risk score). Daily huddles drove FCR from 71 % → 84 % in 5 months.",
    outputs: ["Scorecard PDF.", "RAG status roll-up.", "Owner-accountability matrix."],
    pitfalls: [
      "Vanity KPIs (Likes, Visits) — choose actionable, owned metrics.",
      "Too many — cap at 5–7 per tier.",
      "No cadence — board-on-wall without weekly review = wallpaper.",
    ],
    furtherReading: [
      { label: "The Balanced Scorecard", ref: "Kaplan & Norton, 1996" },
      { label: "Measure What Matters", ref: "John Doerr, 2018" },
    ],
  },

  tier_meeting: {
    name: "Tier Meeting Designer",
    category: "Sustain",
    icon: "Users",
    tagline: "Tier-1/2/3 huddles — agenda, timing, escalation triggers.",
    purpose:
      "Design tiered daily / weekly huddles (Tier-1 operator, Tier-2 supervisor, Tier-3 manager) with explicit agenda items, time allocations, owners and clear escalation triggers up the tier ladder.",
    history:
      "Tiered huddles emerged from Toyota's andon-cord escalation discipline; codified in Lean Hospitals (Mark Graban, 2008) and HPW workshops; now a global daily-management standard.",
    whenToUse: [
      "Building a daily-management system",
      "Cascading strategy from board to floor",
      "Resolving cross-functional issues at the right level fast",
    ],
    howToUse: [
      "Per tier: agenda items, time-box, owner, escalation rules.",
      "Linked SQDC (Safety, Quality, Delivery, Cost) board with KPI traffic-lights.",
      "Document escalation triggers (e.g. red KPI 2 days running → Tier-3).",
    ],
    example:
      "Hospital ward Tier-1 huddle: 8 min, 5 SQDC items, supervisor leads. Tier-2 weekly 25 min — lifted ward NPS by 18 points in one quarter.",
    outputs: ["Huddle agenda PDFs per tier.", "SQDC board template.", "Escalation matrix."],
    pitfalls: [
      "Huddles drift to 30 min status updates — enforce time-box and stand-up format.",
      "No clear escalation rule — issues fester at Tier-1.",
    ],
    furtherReading: [
      { label: "Lean Hospitals", ref: "Mark Graban, 2008" },
    ],
  },

  leader_standard_work: {
    name: "Leader Standard Work (LSW)",
    category: "Sustain",
    icon: "Calendar",
    tagline: "Daily/weekly leader routine — Gemba, coach, audit, review.",
    purpose:
      "Define what 'good leadership looks like' as a structured daily / weekly routine: Gemba walks, coaching dialogues, audit reviews, KPI reviews — so leadership behaviour reinforces the operating system rather than fire-fighting.",
    history:
      "Documented at Toyota since the 1970s; popularised by David Mann's 'Creating a Lean Culture' (2005) — LSW is the single biggest predictor of sustained Lean transformation.",
    whenToUse: [
      "Post-Lean rollout sustainability",
      "Onboarding new managers",
      "Auditing why a transformation is regressing",
    ],
    howToUse: [
      "Per leader role: list daily and weekly tasks with time-box.",
      "Target Gemba % (typical 20–30 % for managers).",
      "Coaching dialogues & audit cadence checks per role.",
    ],
    example:
      "Plant manager LSW: 60 min Gemba/day, 2 audit reviews/week, 1 coaching dialogue/day. After 90 days the plant's safety incident rate fell 38 %.",
    outputs: ["LSW worksheet PDF per role.", "Gemba % tracker."],
    pitfalls: [
      "LSW becomes a checklist, not coaching — teach the why behind each routine.",
      "Skipping Gemba time when 'urgent things come up' — Gemba IS the urgent thing.",
    ],
    furtherReading: [
      { label: "Creating a Lean Culture", ref: "David Mann, 2005" },
    ],
  },

  lpa_scheduler: {
    name: "Layered Process Audit (LPA) Scheduler",
    category: "Sustain",
    icon: "ClipboardCheck",
    tagline: "Multi-layer audit calendar — Operator, Supervisor, Manager, Plant.",
    purpose:
      "Schedule and track Layered Process Audits — a quality-system practice where successive layers of leadership audit the same critical process points at decreasing frequency, ensuring standards don't drift.",
    history:
      "Originated at Daimler-Chrysler in the late 1990s as an answer to recurring quality escapes; now mandated by the IATF 16949 automotive standard and adopted across regulated industries.",
    whenToUse: [
      "Sustaining gains after a Kaizen / DMAIC implementation",
      "IATF 16949 / FDA / ISO compliance",
      "After SOX-style governance reviews flag drift",
    ],
    howToUse: [
      "Define audit layers (Operator daily, Supervisor weekly, Manager monthly, Plant quarterly).",
      "Per layer: questions, standard expected, frequency.",
      "Capture compliance % per audit; trend monthly.",
    ],
    example:
      "Tier-1 supplier LPA programme drove first-time-quality from 96.4 % → 99.1 % over 6 months — and earned IATF re-certification.",
    outputs: ["Audit calendar PDF.", "Compliance % trend.", "Layer-wise findings register."],
    pitfalls: [
      "Audits become 'tick-box' — rotate questions and inspect answers.",
      "Findings without owner & due-date — they don't close.",
    ],
    furtherReading: [
      { label: "Layered Process Audits (CQI Pocket Guide)", ref: "ASQ, 2014" },
    ],
  },

  benefit_tracker: {
    name: "Benefit Realisation Tracker",
    category: "Sustain",
    icon: "DollarSign",
    tagline: "Committed vs Actual savings — month by month, initiative by initiative.",
    purpose:
      "Track committed savings vs actuals per initiative per period, compute attainment %, and surface drift before it becomes a write-off — so transformation programmes deliver the business case they sold.",
    history:
      "Project Management Institute's PMBOK has tracked benefits since the 1990s; the Lean & Six-Sigma community (especially McKinsey OPS practice) elevated the practice to programme-board ritual in the 2000s.",
    whenToUse: [
      "After ROI business cases approved — to ensure they land",
      "Programme steering-committee reporting",
      "CFO finance-of-Lean alignment",
    ],
    howToUse: [
      "Per initiative: monthly committed vs actual savings.",
      "Attainment % = actual / committed.",
      "Roll up by programme to show portfolio health.",
    ],
    example:
      "Lean Wave-1 portfolio committed US$ 14 M; tracker showed 87 % attainment at month 9 — 3 initiatives flagged red triggered re-baselining and recovered US$ 1.2 M.",
    outputs: ["Initiative-level tracker PDF.", "Portfolio attainment %.", "Drift alerts."],
    pitfalls: [
      "Counting paper savings — finance-validate every line.",
      "Re-baselining silently — keep a versioned baseline log.",
    ],
    furtherReading: [
      { label: "Standard for Program Management (PMI)", ref: "PMI, 4th ed., 2017" },
    ],
  },

  iluo_matrix: {
    name: "Skills / ILUO Matrix",
    category: "People",
    icon: "Grid3x3",
    tagline: "Versatility heat-map — who can do what, at what depth.",
    purpose:
      "Map every person in a team against every critical task / skill at four levels: I (Initial / aware), L (Learning / supervised), U (Uses / proficient), O (Owns / can train others) — to expose training gaps and risk concentration.",
    history:
      "Toyota Skills Matrix tradition (1960s); standardised by Yamaha and Honda HR in the 1980s; now a Lean & TWI (Training Within Industry) cornerstone.",
    whenToUse: [
      "Cross-training planning — how to lift versatility %",
      "Single-point-of-failure risk assessment",
      "Onboarding new hires — what to learn, in what order",
      "Pre-Yamazumi rebalance — verify operators have the skills",
    ],
    howToUse: [
      "Rows = people, columns = skills/tasks. Each cell shows the ILUO level.",
      "Aggregate versatility % per person and coverage % per skill.",
      "Flag tasks with < 2 'O' level coverage as single-point risk.",
    ],
    example:
      "Mortgage-ops team of 12 had 'Underwriting Tier-2' with only 1 'O' — moved 2 people from 'U' → 'O' over 60 days through structured shadowing; eliminated the SPOF.",
    outputs: ["ILUO matrix PDF.", "Versatility % per person & per skill.", "SPOF report."],
    pitfalls: [
      "Self-rated levels inflate — calibrate via observation or test.",
      "Letting matrix go stale — review at each appraisal cycle.",
    ],
    furtherReading: [
      { label: "Training Within Industry (TWI) Job Instruction", ref: "Liker & Meier, 2007" },
    ],
  },

  tna: {
    name: "Training Needs Analysis (TNA)",
    category: "People",
    icon: "GraduationCap",
    tagline: "Capability-vs-demand FTE gap — and the plan to close it.",
    purpose:
      "Quantify the gap between current trained-FTE and demand-driven required-FTE per skill, classify criticality, and design an intervention plan (training, hiring, contractor) per skill gap.",
    history:
      "TNA emerged from the US Army's WWII Training Within Industry (TWI) programmes; refined by Donald Kirkpatrick's four-level training evaluation model (1959). Now standard in HR Learning & Development frameworks (CIPD, ATD).",
    whenToUse: [
      "Annual training plan budgeting",
      "After ILUO matrix reveals gaps",
      "Workforce-plan handoff into L&D",
    ],
    howToUse: [
      "List skills with current trained FTE & demand-driven required FTE.",
      "Gap = required − current; flag criticality (High / Med / Low).",
      "Design intervention per skill: hire / train / contract / re-skill.",
    ],
    example:
      "Bank Ops TNA: 5 skills had material gaps; 3 closed via training, 1 via hiring, 1 via outsourcing — eliminated 22 FTE-month capacity shortfall.",
    outputs: ["TNA register PDF.", "Intervention plan with budget.", "Criticality heat-map."],
    pitfalls: [
      "TNA without demand model — gaps based on opinion.",
      "Treating all gaps the same — a Critical-skill 1-FTE gap > 20-FTE Low-skill gap.",
    ],
    furtherReading: [
      { label: "Training in Organizations", ref: "Goldstein & Ford, 2002" },
    ],
  },

  adkar: {
    name: "Change Readiness (ADKAR)",
    category: "People",
    icon: "Users2",
    tagline: "Stakeholder readiness across 5 gates: Awareness · Desire · Knowledge · Ability · Reinforcement.",
    purpose:
      "Score each stakeholder group across the five ADKAR dimensions to predict change readiness and design targeted interventions for the weakest gate per group.",
    history:
      "ADKAR developed by Jeff Hiatt at Prosci (1998) from research on > 700 change projects. Now the leading individual-change model in the corporate change-management profession.",
    whenToUse: [
      "Pre-go-live for any process / system change",
      "Diagnosing stalled adoption post-rollout",
      "Targeted change-comms planning per stakeholder group",
    ],
    howToUse: [
      "List stakeholder groups (e.g. Branch managers, Tellers, Customers).",
      "Score each on Awareness, Desire, Knowledge, Ability, Reinforcement (1–5).",
      "Heat-map shows weakest gate per group → tailored intervention.",
    ],
    example:
      "Core-banking migration: Tellers scored Awareness 5 / Desire 2 — re-pivoted comms from 'what' to 'why this benefits you' for 30 days; Desire lifted to 4 and adoption rose 38 %.",
    outputs: ["ADKAR heat-map.", "Per-group intervention plan.", "Change-comms calendar."],
    pitfalls: [
      "Self-scored without dialogue — interview a sample.",
      "One-size-fits-all comms — different groups have different weakest gates.",
    ],
    furtherReading: [
      { label: "ADKAR: A Model for Change", ref: "Jeff Hiatt, 2006" },
    ],
  },

  lean_academy: {
    name: "Lean Academy Tracker",
    category: "People",
    icon: "Award",
    tagline: "Yellow / Green / Black belt programme — roster, status, certification.",
    purpose:
      "Track the Lean / Six-Sigma capability programme — roster of learners, belt level, project status, certification dates and trainer assignments — to grow an internal capability bench.",
    history:
      "Belts borrowed from martial arts terminology by Bill Smith at Motorola (1987) for Six-Sigma; expanded by GE (1995) to scale internal Black-Belt practitioners.",
    whenToUse: [
      "Building internal Lean capability beyond external consultants",
      "HR talent pipeline / succession planning",
      "Programme-level capability KPIs",
    ],
    howToUse: [
      "Add learners with belt level (Yellow / Green / Black), project, mentor.",
      "Track training progress, project completion, certification.",
      "Roll up by belt level and unit.",
    ],
    example:
      "Industrial group's Lean Academy certified 14 Green Belts and 3 Black Belts in Year-1; their projects delivered US$ 3.2 M validated savings — > 4× programme cost.",
    outputs: ["Roster PDF.", "Belt-level pipeline funnel.", "Certification dashboard."],
    pitfalls: [
      "Certifying without project completion — belt without ROI.",
      "No mentor assigned — completion rates collapse.",
    ],
    furtherReading: [
      { label: "Six Sigma: The Breakthrough Management Strategy", ref: "Harry & Schroeder, 2000" },
    ],
  },

  erlang_c: {
    name: "Call-Centre Sizing (Erlang C)",
    category: "Domain",
    icon: "Phone",
    tagline: "Service-level driven agent sizing — for the maths-grounded call centre.",
    purpose:
      "Compute the agent count required to meet a target service level (e.g. 80 % of calls answered within 20 sec) given call volume, AHT, shrinkage and target ASA — using the Erlang C queuing formula.",
    history:
      "Agner Krarup Erlang published his eponymous queuing formulae at the Copenhagen Telephone Company (1909–1917). Erlang C handles wait-and-be-served queues; remains the textbook formula for inbound contact-centre staffing.",
    whenToUse: [
      "Inbound call-centre / chat sizing per interval",
      "Service-level vs cost trade-off analysis",
      "Outsourcing RFP scrutiny",
    ],
    howToUse: [
      "Capture interval call volume, AHT, target SL%, target ASA, shrinkage %.",
      "Tool computes erlangs, raw agents, shrinkage-grossed FTE.",
      "Run scenarios (peak vs avg) and compare occupancy.",
    ],
    example:
      "Insurance contact-centre: 320 calls/30-min, AHT 240 sec, 80/20 SL, 28 % shrinkage → 38 raw agents → 53 FTE on-roster; matches actual to ±2 %.",
    outputs: ["Sizing calculator PDF.", "SL-vs-FTE curve.", "Occupancy forecast."],
    pitfalls: [
      "Using daily averages — Erlang requires interval data (15–60 min).",
      "Assuming Erlang C for outbound — outbound uses Erlang B.",
      "Missing shrinkage — under-sizes by 15–35 %.",
    ],
    furtherReading: [
      { label: "Call Center Management on Fast Forward", ref: "Cleveland, 2012" },
    ],
  },

  monte_carlo_queue: {
    name: "Queue / Capacity Monte-Carlo Simulator",
    category: "Domain",
    icon: "Sigma",
    tagline: "When Erlang's assumptions break — simulate the full distribution.",
    purpose:
      "Simulate arrival and service-time distributions stochastically (Monte Carlo) to model wait times, queue length, P95 and capacity stress — when call patterns or service times don't fit Erlang's Poisson/exponential assumptions.",
    history:
      "Monte Carlo simulation invented by Stanislaw Ulam & John von Neumann (Manhattan Project, 1946); applied to operations research throughout the 1950s; now standard for non-Markovian queue modelling.",
    whenToUse: [
      "Heavy-tailed AHT distributions (e.g. complex case-handling)",
      "Bursty arrivals (campaigns, weather events)",
      "Multi-skill / re-routing scenarios",
    ],
    howToUse: [
      "Capture arrival distribution parameters and service-time distribution.",
      "Run N replications (typically 10 000+).",
      "Read mean wait time, P95, max queue length.",
      "Vary capacity to find the SL knee-point.",
    ],
    example:
      "Bank branch with bimodal arrival (lunch + evening peaks): Erlang C predicted 3 tellers; Monte Carlo showed P95 wait ballooning at 3 tellers → re-sized to 4 with split shifts; abandonment fell 64 %.",
    outputs: ["Wait-time distribution histogram.", "P95 vs capacity curve."],
    pitfalls: [
      "Using too few replications — < 1000 produces unstable percentiles.",
      "Wrong distribution choice — fit empirical data; don't assume exponential.",
    ],
    furtherReading: [
      { label: "Simulation Modeling and Analysis", ref: "Law, 5th ed., 2014" },
    ],
  },

  attrition_cohort: {
    name: "Attrition Cohort Modeller",
    category: "Domain",
    icon: "TrendingDown",
    tagline: "Headcount cohorts — hiring waves vs attrition shocks, month by month.",
    purpose:
      "Forecast headcount over time using cohort retention curves, hiring waves and attrition shocks — to predict capacity gaps and inform hiring plans before they bite.",
    history:
      "Demographic-cohort analysis is a classic actuarial technique (Lee, 1973). Adapted to workforce planning by SHRM and Bersin in the 2000s as turnover became a board-level KPI.",
    whenToUse: [
      "High-attrition operations (BPO, retail, tech)",
      "Workforce planning across 12+ months",
      "Stress-testing growth plans against turnover risk",
    ],
    howToUse: [
      "Capture starting headcount + monthly attrition curve per tenure cohort.",
      "Schedule hiring waves with onboarding lag.",
      "Run shock scenarios (e.g. +5 pp attrition spike).",
      "Read forecast vs target headcount.",
    ],
    example:
      "BPO 2 800 FTE forecast: baseline 26 % annual attrition; modeller showed Q3 capacity dropping to 92 % of plan even with steady hiring → moved 60 hires forward to maintain SLA.",
    outputs: ["Headcount-over-time chart.", "Cohort retention curves.", "Stress-test scenarios."],
    pitfalls: [
      "Single attrition rate across all tenures — first-90-day attrition is typically 2–4× higher.",
      "Ignoring onboarding ramp — new hires are not 100 % productive day 1.",
    ],
    furtherReading: [
      { label: "Workforce Asset Management Book of Knowledge", ref: "IHRIM, 2012" },
    ],
  },

  automation_scorer: {
    name: "Automation Opportunity Scorer",
    category: "Domain",
    icon: "Bot",
    tagline: "Multi-criteria RPA / AI / BPM candidate ranking.",
    purpose:
      "Score candidate processes across multiple criteria — volume, rule-based-ness, structure, error rate, business value — to rank automation candidates objectively and avoid 'bot-stamp-collecting' anti-patterns.",
    history:
      "Emerged from the RPA-adoption wave (Blue Prism, UiPath, Automation Anywhere) in the 2010s; refined by analyst frameworks (Forrester / HFS) for AI-vs-RPA candidate evaluation.",
    whenToUse: [
      "Building or pruning the automation pipeline",
      "Comparing RPA vs API vs BPM vs ML approaches",
      "Quarterly automation portfolio review",
    ],
    howToUse: [
      "List candidate processes; per process score each criterion (1–5).",
      "Tool computes weighted score and ranks candidates.",
      "Filter by score threshold for the next-quarter pipeline.",
    ],
    example:
      "GCC bank evaluated 47 automation candidates; scorer surfaced top-12 with combined US$ 4.6 M annualised savings; deferred 18 low-score candidates that would have over-extended the platform.",
    outputs: ["Ranked candidate list.", "Score-distribution histogram."],
    pitfalls: [
      "Letting volume dominate — high-value low-volume cases (e.g. exception handling) often have higher ROI.",
      "Scoring without sponsor — without business owner the candidate doesn't move.",
    ],
    furtherReading: [
      { label: "The Robotic Process Automation Handbook", ref: "Tom Taulli, 2020" },
    ],
  },

  customer_journey: {
    name: "Customer Journey Mapper",
    category: "Domain",
    icon: "Map",
    tagline: "Stage × touchpoint emotion curve — find the moments that matter.",
    purpose:
      "Map a customer (or employee) journey across stages and touchpoints, capture the emotion / sentiment at each step, and identify pain points and design opportunities at the lowest emotion points.",
    history:
      "Journey mapping evolved from service-design (Engine Group, IDEO, Live|Work) in the 2000s; canonised in 'This Is Service Design Thinking' (Stickdorn, 2011). Adobe / Salesforce now embed it in CXM platforms.",
    whenToUse: [
      "Designing or redesigning a service / digital product",
      "Diagnosing CX / NPS drops",
      "Persona-led product strategy workshops",
    ],
    howToUse: [
      "Define personas; per persona list journey stages.",
      "At each touchpoint capture action, thought, emotion (−2 to +2).",
      "Surface pain-points at low-emotion troughs and design opportunities at peaks.",
    ],
    example:
      "New-account opening journey: 6 stages, lowest emotion at 'KYC document upload' (-2). Redesign added a single document portal; emotion lifted to +1 and abandonment fell from 22 % to 6 %.",
    outputs: ["Journey map PDF (landscape).", "Emotion curve.", "Top-pain-points / top-delight register."],
    pitfalls: [
      "Internal-perspective journeys — observe real users.",
      "One persona only — segment by behaviour, not just demographics.",
    ],
    furtherReading: [
      { label: "This Is Service Design Doing", ref: "Stickdorn et al., 2018" },
    ],
  },

  dilo: {
    name: "Day In the Life Of (DILO)",
    category: "Diagnose",
    icon: "Clock",
    tagline: "Sequential activity log of a role's day — VA / NVA / BNVA / Wait.",
    purpose:
      "Shadow a role for one or more days and log every activity in sequence with start/end times and VA classification, producing a high-resolution picture of how time is actually spent — usually a stark contrast with what people believe.",
    history:
      "DILO studies were pioneered in management-consulting practice (McKinsey, Bain) in the 1990s as a fast diagnostic for white-collar roles; now ubiquitous in operating-model redesign engagements.",
    whenToUse: [
      "Diagnosing manager / knowledge-worker capacity",
      "Spans-and-layers redesign",
      "Pre-RACI / role-clarity workshops",
    ],
    howToUse: [
      "Shadow the role for 1–3 representative days.",
      "Log every activity start, end, classification, and notes.",
      "Roll up: VA %, NVA %, meeting %, admin % — and compare to role expectations.",
    ],
    example:
      "Branch manager DILO: 41 % of time in admin, 18 % in coaching (target: 35 %). Acted on by removing 6 reports + redesigning meeting cadence; coaching share rose to 31 % in 8 weeks.",
    outputs: ["Activity log PDF.", "VA-share roll-up.", "Time-allocation pie."],
    pitfalls: [
      "Hawthorne effect — shadow at least 2 days and brief honestly.",
      "Aggregating across very different role-types — analyse like-for-like.",
    ],
    furtherReading: [
      { label: "Time, Talent, Energy", ref: "Mankins & Garton (Bain), 2017" },
    ],
  },

  five_s_audit: {
    name: "5S Workplace Audit",
    category: "Sustain",
    icon: "CheckSquare",
    tagline: "Sort · Set in order · Shine · Standardise · Sustain — score and act.",
    purpose:
      "Score a workplace (physical or digital) on the five S pillars with domain-specific questionnaires; produce a remediation tracker and trend score over time as the foundation of a Lean operating environment.",
    history:
      "5S developed at Toyota (1972), formalised by Hiroyuki Hirano (1995); the foundational Lean technique adopted in offices, hospitals, government and IT.",
    whenToUse: [
      "Day 1 of any Lean rollout — visual baseline",
      "Quarterly sustenance audits",
      "Pre-Kaizen-event workplace prep",
    ],
    howToUse: [
      "Choose your domain template (Insurance, Banking, Healthcare/BPO, Telecom, Retail, Logistics, Government, Generic, Custom).",
      "Walk the area; score each item 0–5 per pillar.",
      "Add remediation actions for low scores; track over time.",
    ],
    example:
      "Bank back-office 5S: Sort 2.1 → 4.2 in 30 days through ruthless decluttering; Standardise lifted last from 1.3 → 3.9 via labelled storage protocol.",
    outputs: ["Per-pillar scored audit PDF.", "Remediation tracker.", "Trend over time."],
    pitfalls: [
      "Doing 4S — Sustain is the hardest and most-skipped pillar.",
      "5S without LSW + audits — gains decay in 6–8 weeks.",
    ],
    furtherReading: [
      { label: "5 Pillars of the Visual Workplace", ref: "Hirano, 1995" },
    ],
  },

  maturity_assessment: {
    name: "Lean Maturity Assessment",
    category: "Sustain",
    icon: "TrendingUp",
    tagline: "5-level maturity grading across customisable Lean pillars.",
    purpose:
      "Grade an organisation across customisable Lean pillars (typical: Leadership, Strategy, Voice of Customer, Process, People, Technology) at five levels (Initial → Optimising), comparing current to target, and producing a gap-sorted action backlog.",
    history:
      "Maturity-model concept from Carnegie Mellon's CMMI (1986); adapted to Lean by Shingo Institute, EFQM and the Lean Enterprise Institute in the 2000s.",
    whenToUse: [
      "Setting a multi-year Lean transformation roadmap",
      "Benchmarking sites or business units",
      "Steering-committee progress reviews",
    ],
    howToUse: [
      "Choose pillars and 5-level rubric.",
      "Score current vs target per pillar.",
      "Radar chart visualises gap; gap-sorted action table guides backlog.",
    ],
    example:
      "Retail group's 6 stores assessed against 6 pillars; Strategy at 1.8 vs target 4 — drove a hoshin cascade workshop and standardised KPI tree across stores.",
    outputs: ["Radar chart current vs target.", "Action table sorted by gap.", "Pillar maturity %."],
    pitfalls: [
      "Self-scoring inflation — calibrate via external assessor or peer review.",
      "Targeting Level 5 across all pillars — focus on 2–3 critical pillars per year.",
    ],
    furtherReading: [
      { label: "The Shingo Model", ref: "Shingo Institute, 2020" },
    ],
    chart: "radar",
  },
};

// Export as ordered list to drive index pages
export const TOOL_DOCS = Object.entries(D).map(([id, doc]) => ({ id, ...doc }));
export const TOOL_DOCS_BY_ID = D;
export default D;
